import * as Sentry from '@sentry/remix';
import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { getAppGlobals } from '../AppGlobals/utils';
import FallBack from '../Fallback';
const appGlobals = getAppGlobals();

const ErrorBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    if (isRouteErrorResponse(error)) return;
    Sentry.captureException(error, {
      tags: {
        keyword: 'remix-error-boundary',
      },
    });
  }, [error]);

  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 400: {
        return <FallBack errorCode="400" publicationPresent={false} />;
      }
      case 404: {
        return (
          <FallBack
            errorCode="404"
            publicationPresent={false}
            customTitle={error.data === 'DirectLinkExpired' ? 'Uh oh, it looks like this link is expired.' : undefined}
            customDescription={
              error.data === 'DirectLinkExpired' ? (
                <>The Publication needs to update a few things on their end to resume.</>
              ) : undefined
            }
          />
        );
      }
      default: {
        return <FallBack errorCode={`${error.status}`} error={error} />;
      }
    }
  }

  // default to client error
  let errorCode = '0';
  if (error instanceof Error) {
    let errorMessage = error.message ?? '';
    // if error message is a json string, parse it and extract status and message
    // this is set in `handleError` function in entry.server.tsx
    if (errorMessage.startsWith('{')) {
      try {
        const errorJson = JSON.parse(errorMessage);
        errorCode = errorJson.status ?? '0';
        errorMessage = errorJson.message ?? '';
      } catch (_e) {
        // do nothing
      }
    }
  }

  // To make debugging easier in development.
  // biome-ignore lint/correctness/noConstantCondition: <explanation>
  if (false && process.env.NODE_ENV === 'development' && !appGlobals.ENABLE_SENTRY_DEV) {
    // biome-ignore lint/suspicious/noExplicitAny: Keeping for backward compatibility
    const unknownError: any = error;

    return (
      <div className="p-6 m-6 space-y-4 overflow-scroll text-sm text-red-600 rounded bg-red-50">
        <h1 className="text-lg font-semibold">Exception caught by root error boundary</h1>
        <p>{unknownError?.message}</p>
        <pre>{unknownError?.stack}</pre>
      </div>
    );
  }

  return <FallBack errorCode={errorCode} error={error} />;
};

export default ErrorBoundary;
